import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import { useUser } from '../../context/UserContext';

import UserNav from '../../components/navbar/user-nav';
import AdminSidebar from '../../components/admin-sidebar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import CertificationsManager from '../../components/CertificationsManager';

export default function MyProfile() {
  const [show, setShow] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const auth = getAuth();
  const storage = getStorage();
  const [nombreInmobiliaria, setNombreInmobiliaria] = useState('');
  const { refreshUser } = useUser(); // 👈 Hook para refrescar el contexto


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user?.email) {
        try {
          const qAsesor = query(collection(db, 'asesor'), where('correo', '==', user.email));
          const qAsociado = query(collection(db, 'asociado'), where('correo', '==', user.email));
  
          const [asesorSnap, asociadoSnap] = await Promise.all([
            getDocs(qAsesor),
            getDocs(qAsociado),
          ]);
  
          let userDoc: any = null;
          let tipo = '';
  
          if (!asesorSnap.empty) {
            userDoc = asesorSnap.docs[0];
            tipo = 'asesor';
          } else if (!asociadoSnap.empty) {
            userDoc = asociadoSnap.docs[0];
            tipo = 'asociado';
          }
  
          if (userDoc) {
            const data = userDoc.data();
            const inmobiliariaDocId = tipo === 'asesor'
              ? (Array.isArray(data.inmobiliaria_doc_ids) ? data.inmobiliaria_doc_ids[0] : data.inmobiliaria_doc_ids)
              : data.inmobiliaria_doc_id;

            let nombreInmo = '';
            if (inmobiliariaDocId) {
              // Buscar directamente por doc.id
              const { doc: docFn, getDoc } = await import('firebase/firestore');
              const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
              const inmoSnap = await getDoc(inmoDocRef);
              if (inmoSnap.exists()) {
                nombreInmo = inmoSnap.data().nombre || '';
              }
            }

            setUserData({
              ...data,
              tipo,
              id: userDoc.id,
              inmobiliaria_nombre: nombreInmo
            });
            setNombreInmobiliaria(nombreInmo);
          }
        } catch (err) {
          console.error('Error al cargar perfil:', err);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });
  
    return () => unsubscribe();
  }, []);
  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name.startsWith('redes.')) {
      const key = name.split('.')[1];
      setUserData((prev: any) => ({
        ...prev,
        redes: {
          ...prev.redes,
          [key]: value,
        },
      }));
    } else {
      setUserData((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Por favor selecciona una imagen válida.',
        confirmButtonColor: '#d33'
      });
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'La imagen no debe superar los 5MB.',
        confirmButtonColor: '#d33'
      });
      return;
    }

    try {
      setUploadingPhoto(true);

      // Crear preview local
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Subir a Firebase Storage
      const timestamp = Date.now();
      const fileName = `${userData.tipo}/${userData.id}/profile_${timestamp}.${file.name.split('.').pop()}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Actualizar Firestore
      const docRef = doc(db, userData.tipo, userData.id);
      await updateDoc(docRef, { foto: downloadURL });

      // Actualizar estado local
      setUserData((prev: any) => ({
        ...prev,
        foto: downloadURL
      }));

      // 🔄 Refrescar el contexto del usuario para actualizar la foto en toda la app
      await refreshUser();

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Foto de perfil actualizada correctamente.',
        confirmButtonColor: '#3085d6'
      });

    } catch (error) {
      console.error('Error al subir foto:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al subir la foto. Inténtalo de nuevo.',
        confirmButtonColor: '#d33'
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData || !userData.tipo || !userData.id) return;

    try {
      const ref = doc(db, userData.tipo, userData.id);
      await updateDoc(ref, userData);
      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Cambios guardados exitosamente.',
        confirmButtonColor: '#3085d6'
      });
    } catch (error) {
      console.error('Error al guardar:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al guardar los cambios.',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (loading || !userData) return <div className="text-center p-5">Cargando datos del perfil...</div>;

  return (
    <>
<UserNav
  nombreUsuario={userData?.nombre || 'Desconocido'}
  nombreInmobiliaria={userData?.inmobiliaria_nombre || 'Inmobiliaria no asignada'}
/>

      <div className="page-title">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <h2 className="ipt-title">Edita tu Perfil Profesional</h2>
              <span className="ipn-subtitle">Completa y mantén actualizada tu información para aumentar tu visibilidad.</span>
            </div>
          </div>
        </div>
      </div>

      <section className="py-5">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="filter_search_opt">
                <Link to="#" className="btn btn-dark full-width mb-4" onClick={() => setShow(!show)}>
                  Dashboard Navigation<i className="fa-solid fa-bars ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-md-12">
            <AdminSidebar
  show={show}
  setShow={setShow}
  nombre={userData?.nombre}
  inmobiliaria={userData?.inmobiliaria_nombre}
/>            </div>

            <div className="col-lg-9 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <form onSubmit={handleSubmit} className="taller-form-modern">
                  {/* Sección de Foto de Perfil */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">
                        <i className="fa-solid fa-camera me-2"></i>
                        Foto de Perfil
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-4 text-center mb-3 mb-md-0">
                          <div className="position-relative d-inline-block">
                            {photoPreview || userData.foto ? (
                              <img
                                src={photoPreview || userData.foto}
                                alt="Foto de perfil"
                                className="rounded-circle"
                                style={{
                                  width: '150px',
                                  height: '150px',
                                  objectFit: 'cover',
                                  border: '3px solid #ddd'
                                }}
                              />
                            ) : (
                              <div
                                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                style={{
                                  width: '150px',
                                  height: '150px',
                                  border: '3px solid #ddd'
                                }}
                              >
                                <i className="fa-solid fa-user fa-4x text-muted"></i>
                              </div>
                            )}
                            {uploadingPhoto && (
                              <div
                                className="position-absolute top-0 start-0 w-100 h-100 rounded-circle d-flex align-items-center justify-content-center"
                                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                              >
                                <div className="spinner-border text-light" role="status">
                                  <span className="visually-hidden">Cargando...</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-8">
                          <h6 className="mb-2">Cambiar foto de perfil</h6>
                          <p className="text-muted small mb-3">
                            Formatos permitidos: JPG, PNG, GIF. Tamaño máximo: 5MB
                          </p>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handlePhotoChange}
                            accept="image/*"
                            className="d-none"
                          />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingPhoto}
                          >
                            <i className="fa-solid fa-upload me-2"></i>
                            {uploadingPhoto ? 'Subiendo...' : 'Seleccionar Foto'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 1: Datos Personales */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">1. Datos Personales</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Nombre</label>
                          <input
                            name="nombre"
                            type="text"
                            className="form-control"
                            value={userData.nombre || ''}
                            onChange={handleChange}
                            placeholder="Ingresa tu nombre completo"
                            required
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>Correo</label>
                          <input
                            name="correo"
                            type="email"
                            className="form-control"
                            value={userData.correo || ''}
                            onChange={handleChange}
                            placeholder="correo@ejemplo.com"
                            required
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>Teléfono</label>
                          <input
                            name="telefono"
                            type="text"
                            className="form-control"
                            value={userData.telefono || ''}
                            onChange={handleChange}
                            placeholder="7711234567"
                            required
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>Años de experiencia</label>
                          <input
                            name="años_experiencia"
                            type="number"
                            className="form-control"
                            value={userData.años_experiencia || ''}
                            onChange={handleChange}
                            placeholder="Ej. 5"
                            required
                          />
                        </div>

                        <div className="form-group col-md-12">
                          <label>Biografía</label>
                          <textarea
                            name="biografia"
                            className="form-control h-120"
                            value={userData.biografia || ''}
                            onChange={handleChange}
                            placeholder="Cuéntanos sobre tu experiencia profesional"
                            required
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 2: Redes Sociales */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">2. Redes Sociales</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Facebook</label>
                          <input
                            name="redes.facebook"
                            type="text"
                            className="form-control"
                            value={userData.redes?.facebook || ''}
                            onChange={handleChange}
                            placeholder="https://facebook.com/..."
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>LinkedIn</label>
                          <input
                            name="redes.linkedin"
                            type="text"
                            className="form-control"
                            value={userData.redes?.linkedin || ''}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/..."
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>Instagram</label>
                          <input
                            name="redes.instagram"
                            type="text"
                            className="form-control"
                            value={userData.redes?.instagram || ''}
                            onChange={handleChange}
                            placeholder="https://instagram.com/..."
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>TikTok</label>
                          <input
                            name="redes.tiktok"
                            type="text"
                            className="form-control"
                            value={userData.redes?.tiktok || ''}
                            onChange={handleChange}
                            placeholder="https://tiktok.com/@..."
                          />
                        </div>

                        <div className="form-group col-md-6">
                          <label>YouTube</label>
                          <input
                            name="redes.youtube"
                            type="text"
                            className="form-control"
                            value={userData.redes?.youtube || ''}
                            onChange={handleChange}
                            placeholder="https://youtube.com/@..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botón de submit */}
                  <div className="text-center mt-4">
                    <button className="btn btn-primary px-5" type="submit">
                      <i className="fa fa-save me-2"></i>
                      Guardar Cambios
                    </button>
                  </div>
                </form>

                {/* Sección 3: Certificaciones (fuera del form) */}
                <div className="card border-0 rounded-4 p-4 shadow-sm mt-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fa-solid fa-certificate me-2"></i>
                      3. Mis Certificaciones
                    </h5>
                  </div>
                  <div className="card-body">
                    <CertificationsManager
                      userId={userData.id}
                      userType={userData.tipo}
                      certificaciones={userData.certificaciones || []}
                      onUpdate={async () => {
                        // Recargar datos del usuario después de actualizar certificaciones
                        const user = auth.currentUser;
                        if (user?.email) {
                          const qAsesor = query(collection(db, 'asesor'), where('correo', '==', user.email));
                          const qAsociado = query(collection(db, 'asociado'), where('correo', '==', user.email));

                          const [asesorSnap, asociadoSnap] = await Promise.all([
                            getDocs(qAsesor),
                            getDocs(qAsociado),
                          ]);

                          let userDoc: any = null;
                          let tipo = '';

                          if (!asesorSnap.empty) {
                            userDoc = asesorSnap.docs[0];
                            tipo = 'asesor';
                          } else if (!asociadoSnap.empty) {
                            userDoc = asociadoSnap.docs[0];
                            tipo = 'asociado';
                          }

                          if (userDoc) {
                            const data = userDoc.data();
                            setUserData((prev: any) => ({
                              ...prev,
                              certificaciones: data.certificaciones || []
                            }));
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
